/**
 * Classifies wallet/transaction error messages into a bounded category set used for
 * log metadata (BetterStack), analytics events (PostHog), and error reporter context
 * (Sentry). Pure string matching — safe to call from any environment.
 */

const REVERT_ERROR_NAMES: ReadonlySet<string> = new Set([
  'ExecutionRevertedError',
  'ContractFunctionRevertedError'
])

/** viem error names for failures that never reached the EVM; any of these in the chain wins. */
const TRANSPORT_ERROR_NAMES: ReadonlySet<string> = new Set([
  'HttpRequestError',
  'TimeoutError',
  'WebSocketRequestError',
  'SocketClosedError'
])

/**
 * Suffix `/api/rpc/[chainId]` appends to a non-terminal JSON-RPC error envelope it passes through
 * (over HTTP 200) after exhausting retries against a failing upstream, e.g. `— Upstream RPC error
 * (HTTP 503)`, `— Upstream RPC timeout`, `— Upstream rate limited`. It is the only transport
 * signal that survives the proxy, so it must be checked before matching revert text.
 */
const PROXY_UPSTREAM_FAILURE_PATTERN =
  /\bupstream (rpc (error|timeout|unavailable)|rate limited)\b/i

const EXECUTION_REVERTED_RPC_CODE = 3

function errorName(error: object) {
  return 'name' in error && typeof error.name === 'string' ? error.name : undefined
}

/** Returns the unique object nodes in an error's `cause` chain, stopping on cycles. */
export function errorChain(error: unknown): object[] {
  const chain: object[] = []
  let current: unknown = error
  while (current && typeof current === 'object' && !chain.includes(current)) {
    chain.push(current)
    current = 'cause' in current ? current.cause : undefined
  }
  return chain
}

/** Whether one cause-chain node signals a failure that did not reach the EVM. */
export function isTransportFailure(error: object): boolean {
  const name = errorName(error)
  if (name && TRANSPORT_ERROR_NAMES.has(name)) return true
  if ('status' in error && typeof error.status === 'number') return true
  return (
    'message' in error && typeof error.message === 'string' && isProxyUpstreamFailure(error.message)
  )
}

/** Whether a message contains the RPC proxy's upstream-failure annotation. */
export function isProxyUpstreamFailure(message: string): boolean {
  return PROXY_UPSTREAM_FAILURE_PATTERN.test(message)
}

function isRevertLeaf(error: object): boolean {
  const name = errorName(error)
  if (name && REVERT_ERROR_NAMES.has(name)) return true
  if ('code' in error && error.code === EXECUTION_REVERTED_RPC_CODE) return true
  return error instanceof Error && error.message.toLowerCase().includes('execution reverted')
}

export function isExecutionRevertError(error: unknown): boolean {
  const chain = errorChain(error)
  if (chain.some(isTransportFailure)) return false
  return chain.some(isRevertLeaf)
}

// Categories are declared in priority order (first-match-wins). `unknown` is the terminal
// fallback with no patterns. `gas_issue` is declared last because its remaining substrings
// (`gas required`, `nonce`, `replacement transaction underpriced`) are broader than the
// more specific categories above and would otherwise steal matches.
// String patterns must be lowercase — the message is lowercased once before matching.
const ERROR_CATEGORY_PATTERNS = {
  user_rejected: [
    'user rejected',
    'user denied',
    'user cancelled',
    'user canceled',
    'action_rejected',
    'rejected the request',
    'request rejected',
    'transaction was rejected',
    'signature denied',
    'rejected by user',
    'userrejectedrequesterror',
    'user disconnected',
    // EIP-1193 user rejection code — word boundary avoids false positives
    // on hex substrings (tx hashes, addresses) that contain "4001".
    /\b4001\b/
  ],
  slippage: ['slippage', 'price impact', 'price changed', 'excessive slippage'],
  transaction_reverted_onchain: [
    'reverted on-chain',
    'transaction reverted',
    'transaction failed: 0x'
  ],
  simulation_failed: ['revert', 'execution reverted', 'call exception', 'simulat'],
  safe_wallet_expired: ['safe transaction expired'],
  safe_wallet_replaced: ['safe transaction replaced'],
  timeout_receipt_polling: [
    'waitfortransactionreceipt',
    'waiting for transaction',
    'to be confirmed'
  ],
  timeout_rpc_request: ['timeout', 'timed out', 'etimedout', 'request took too long'],
  rate_limit: [
    'rate limit',
    'being rate limited',
    'too many requests',
    /\b429\b/,
    'exceeds defined limit'
  ],
  wallet_response_error: [
    'disconnected port',
    'extension context invalidated',
    'wallet extension',
    'extension has not been configured',
    'has not been authorized yet',
    'wkwebview api client did not respond',
    'onekeyerror',
    'switchchainnotsupportederror',
    'does not support programmatic chain switching',
    'failed to sign',
    'invalid signature',
    'ledger',
    'device is unlocked',
    'device must be opened',
    'hardware wallet',
    'trezor',
    'failed to publish payload',
    'unexpected response type'
  ],
  network_error: [
    'failed to fetch',
    'load failed',
    'networkerror',
    'network error',
    'network request failed',
    'econnrefused',
    'enotfound',
    'econnreset',
    'internal error was received',
    'unknown rpc error',
    'resource not available',
    'http request failed',
    'protocol is not supported',
    // WalletConnect / network noise
    'pairing already exists',
    'no matching key',
    'proposal expired',
    'session expired',
    'socket stalled',
    'websocket connection failed',
    'connection closed',
    'connection interrupted',
    'the network connection was lost',
    'the operation was aborted',
    'aborterror'
  ],
  invalid_state: [
    'executerequirement failed',
    'executebundle failed',
    'chain id mismatch',
    'does not match the target chain',
    'account mismatch',
    'address is missing',
    'invalid argument',
    'invalid parameters',
    'missing or invalid',
    'hex string has length'
  ],
  gas_issue: [
    'insufficient funds',
    'gas required',
    'exceeds block gas limit',
    // viem `EstimateGasExecutionError` when an RPC node's `gascap` rejects — MKT-694.
    'exceeds max transaction gas limit',
    'intrinsic gas too low',
    'out of gas',
    'nonce',
    'replacement transaction underpriced'
  ],
  unknown: []
} as const satisfies Record<string, readonly (string | RegExp)[]>

export type TransactionErrorCategory = keyof typeof ERROR_CATEGORY_PATTERNS

const TRANSACTION_ERROR_CATEGORIES = Object.keys(
  ERROR_CATEGORY_PATTERNS
) as readonly TransactionErrorCategory[]

export function classifyTransactionError(errorMessage: string): TransactionErrorCategory {
  const message = errorMessage.toLowerCase()
  for (const category of TRANSACTION_ERROR_CATEGORIES) {
    const patterns = ERROR_CATEGORY_PATTERNS[category]
    if (patterns.some(p => (typeof p === 'string' ? message.includes(p) : p.test(message)))) {
      return category
    }
  }
  return 'unknown'
}
